import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

function startOfLocalDay(date = new Date()): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

interface SelectedDateContextValue {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  goToToday: () => void;
}

const SelectedDateContext = createContext<SelectedDateContextValue | null>(null);

export const SelectedDateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedDate, setSelectedDateState] = useState(() => startOfLocalDay());

  const setSelectedDate = useCallback((date: Date) => {
    setSelectedDateState(startOfLocalDay(date));
  }, []);

  const goToToday = useCallback(() => {
    setSelectedDateState(startOfLocalDay());
  }, []);

  const value = useMemo(
    () => ({ selectedDate, setSelectedDate, goToToday }),
    [goToToday, selectedDate, setSelectedDate],
  );

  return <SelectedDateContext.Provider value={value}>{children}</SelectedDateContext.Provider>;
};

export function useSelectedDate(): SelectedDateContextValue {
  const ctx = useContext(SelectedDateContext);
  if (!ctx) throw new Error('useSelectedDate must be used within a SelectedDateProvider');
  return ctx;
}
